import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { CheckCircle2, Circle, LogOut, Loader2, ArrowRight, CalendarClock } from "lucide-react";

export function ExitSettlement({ agentId, onChanged }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [doneOpen, setDoneOpen] = useState(false);
  const [form, setForm] = useState({ reason: "", last_working_day: new Date().toISOString().slice(0, 10) });
  const [fin, setFin] = useState({ refund_amount: "", reference: "", override: false, withheld_reason: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/admin/agents/${agentId}/exit`).then((r) => { setData(r.data); setFin((f) => ({ ...f, refund_amount: String(r.data.refund_amount) })); }), [agentId]);
  useEffect(() => { load(); }, [load]);

  const call = async (path, body = {}) => {
    setBusy(true);
    try {
      await api.post(`/admin/agents/${agentId}/exit/${path}`, body);
      toast.success(path === "start" ? "Exit settlement started - 30-day refund window running" : path === "complete" ? "Settlement completed - deposit refunded, agent exited" : "Exit cancelled - agent active again");
      setStartOpen(false); setDoneOpen(false);
      load(); onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  const s = data.settlement;
  const inProgress = s?.status === "in_progress";
  const completed = s?.status === "completed";

  return (
    <Card className="p-5 mt-4" data-testid="exit-settlement-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-semibold flex items-center gap-2"><LogOut className="w-4 h-4 text-primary" /> Exit settlement</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Deposit is refunded within 30 days of leaving once stock is returned, dues are collected and no disputes remain.</p>
        </div>
        <div className="flex items-center gap-2">
          {inProgress && (
            <Badge variant={s.overdue ? "destructive" : s.days_left <= 7 ? "secondary" : "outline"} className="gap-1" data-testid="exit-deadline-badge">
              <CalendarClock className="w-3 h-3" /> {s.overdue ? `deadline passed ${-s.days_left}d ago` : `${s.days_left} day(s) left · by ${s.deadline}`}
            </Badge>
          )}
          {completed && <Badge data-testid="exit-completed-badge">Exited {s.completed_at?.slice(0, 10)} · refunded {money(s.refund_amount)}</Badge>}
          {!s && isAdmin && <Button size="sm" variant="outline" onClick={() => setStartOpen(true)} data-testid="exit-start-button"><LogOut className="w-3.5 h-3.5 mr-1" /> Start exit</Button>}
          {inProgress && isAdmin && (
            <>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => call("cancel")} data-testid="exit-cancel-button">Cancel exit</Button>
              <Button size="sm" disabled={busy} onClick={() => setDoneOpen(true)} data-testid="exit-complete-button">Complete & refund</Button>
            </>
          )}
        </div>
      </div>

      {!completed && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="exit-checklist">
          {data.steps.map((st) => (
            <Link to={st.link} key={st.key} className={`flex items-start gap-3 rounded-lg border p-3 hover:bg-secondary/40 transition-colors ${st.done ? "border-primary/30" : "border-destructive/30"}`} data-testid={`exit-step-${st.key}`}>
              {st.done ? <CheckCircle2 className="w-5 h-5 text-primary shrink-0" /> : <Circle className="w-5 h-5 text-destructive shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{st.label}</p>
                <p className="text-xs text-muted-foreground">{st.detail}</p>
              </div>
              {!st.done && <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />}
            </Link>
          ))}
          <div className="rounded-lg border border-border p-3 flex items-center justify-between md:col-span-2">
            <span className="text-sm text-muted-foreground">Deposit held · refundable on completion</span>
            <span className="font-display font-bold" data-testid="exit-refund-amount">{money(data.deposit_held)}</span>
          </div>
        </div>
      )}

      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Start exit for {data.agent.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Last working day</Label><Input type="date" value={form.last_working_day} onChange={(e) => setForm({ ...form, last_working_day: e.target.value })} data-testid="exit-last-day-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Reason (optional)</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="exit-reason-input" /></div>
            <p className="text-xs text-muted-foreground">The agent is marked "exiting"; no new stock should be issued. The 30-day refund window starts from the last working day.</p>
          </div>
          <DialogFooter><Button disabled={busy} onClick={() => call("start", form)} data-testid="exit-start-confirm">Start settlement</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={doneOpen} onOpenChange={setDoneOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Complete settlement & refund deposit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!data.all_clear && (
              <p className="text-xs rounded-md bg-destructive/10 text-destructive p-2" data-testid="exit-blockers">
                Checklist incomplete: {data.steps.filter((x) => !x.done).map((x) => x.label).join("; ")}. Tick override to withhold from the deposit instead.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-xs">Refund amount (held {money(data.deposit_held)})</Label><Input type="number" step="0.01" value={fin.refund_amount} onChange={(e) => setFin({ ...fin, refund_amount: e.target.value })} data-testid="exit-refund-input" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Transfer reference</Label><Input value={fin.reference} onChange={(e) => setFin({ ...fin, reference: e.target.value })} data-testid="exit-reference-input" /></div>
            </div>
            {Number(fin.refund_amount) < data.deposit_held && (
              <div className="space-y-1.5"><Label className="text-xs">Reason for withholding {money(data.deposit_held - Number(fin.refund_amount || 0))}</Label><Input value={fin.withheld_reason} onChange={(e) => setFin({ ...fin, withheld_reason: e.target.value })} data-testid="exit-withheld-reason" /></div>
            )}
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox checked={fin.override} onCheckedChange={(v) => setFin({ ...fin, override: !!v })} data-testid="exit-override-checkbox" /> Override incomplete checklist (admin decision recorded)
            </label>
          </div>
          <DialogFooter>
            <Button disabled={busy} onClick={() => call("complete", { ...fin, refund_amount: Number(fin.refund_amount) })} data-testid="exit-complete-confirm">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refund & close account"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
